// "Designed" templates: a complete hand-built page whose editable parts are marked in the HTML:
//   <span data-ed="N">   a text node          <svg|div data-img="N">  an image slot (illustration or visual box)
//   <a data-lnk="N">     a link               <header|section|footer data-sec="N">  a page section
// Customers only ever store plain data (texts, colours, image asset ids, link URLs, section order, effect settings,
// font names). Everything is validated and escaped here, so a customer can never inject markup or script.
export const FX_STYLES = ['dots', 'stars', 'snow', 'bubbles', 'embers', 'confetti', 'network', 'fireflies', 'rain'];
import { insertExtras } from './design-extras.js';
import { businessJsonLd } from './business.js';
export const DESIGN_MAX_TEXT_LENGTH = 2000;
const INDEX_KEY = /^\d{1,4}$/;
const VAR_KEY = /^--[a-z0-9-]{1,40}$/;
const COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*[\d.\s,%/]+\)|hsla?\(\s*[\d.\s,%/deg]+\))$/;
const ASSET_ID = /^[A-Za-z0-9_-]{1,64}$/;
const SAFE_LINK = /^(#[\w-]{0,80}|\/(?!\/)[^\s"'<>\\]{0,300}|https?:\/\/[^\s"'<>\\]{1,500}|mailto:[^\s"'<>\\]{1,200}|tel:[+\d\s()-]{3,30})$/;
export const DESIGN_FONTS = {
    Inter: 'Inter:wght@400;500;600;700;800',
    Poppins: 'Poppins:wght@400;500;600;700;800',
    Montserrat: 'Montserrat:wght@400;500;600;700;800',
    Raleway: 'Raleway:wght@400;500;600;700;800',
    Nunito: 'Nunito:wght@400;600;700;800',
    Lato: 'Lato:wght@400;700;900',
    'DM Sans': 'DM+Sans:wght@400;500;700',
    'Space Grotesk': 'Space+Grotesk:wght@400;500;700',
    Outfit: 'Outfit:wght@400;500;600;700;800',
    Oswald: 'Oswald:wght@400;500;600;700',
    'Bebas Neue': 'Bebas+Neue',
    Anton: 'Anton',
    'Playfair Display': 'Playfair+Display:wght@400;600;700;800',
    'DM Serif Display': 'DM+Serif+Display',
    Lora: 'Lora:wght@400;500;600;700',
    Merriweather: 'Merriweather:wght@400;700;900',
    'Cormorant Garamond': 'Cormorant+Garamond:wght@400;500;600;700',
    'Libre Baskerville': 'Libre+Baskerville:wght@400;700',
    'Abril Fatface': 'Abril+Fatface',
    Pacifico: 'Pacifico',
    'Caveat': 'Caveat:wght@400;600;700',
    'JetBrains Mono': 'JetBrains+Mono:wght@400;600',
    'Press Start 2P': 'Press+Start+2P'
};
export const DEFAULT_FX = {
    mode: 'original',
    style: 'dots',
    count: 60,
    speed: 1,
    size: 3,
    opacity: 0.7,
    colors: ['#ffffff'],
    interact: true
};
export function isSafeColor(value) {
    return typeof value === 'string' && value.length <= 40 && COLOR.test(value.trim());
}
export function isSafeLink(value) {
    return typeof value === 'string' && SAFE_LINK.test(value.trim());
}
export function isValidDesignText(key, value) {
    return INDEX_KEY.test(key) && typeof value === 'string' && value.length <= DESIGN_MAX_TEXT_LENGTH;
}
export function isValidDesignVar(key, value) {
    return VAR_KEY.test(key) && typeof value === 'string' && isSafeColor(value);
}
const clamp = (n, min, max, fallback) => typeof n === 'number' && Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
/** Return a fully valid DesignFx (anything invalid falls back to a default). */
export function normalizeFx(input) {
    const f = (typeof input === 'object' && input !== null ? input : {});
    const mode = f.mode === 'none' || f.mode === 'custom' ? f.mode : 'original';
    const style = FX_STYLES.includes(f.style) ? f.style : DEFAULT_FX.style;
    const colors = Array.isArray(f.colors) ? f.colors.filter((c) => typeof c === 'string' && isSafeColor(c)).slice(0, 4).map((c) => c.trim()) : [];
    return {
        mode,
        style,
        count: Math.round(clamp(f.count, 0, 300, DEFAULT_FX.count)),
        speed: clamp(f.speed, 0.1, 5, DEFAULT_FX.speed),
        size: clamp(f.size, 1, 30, DEFAULT_FX.size),
        opacity: clamp(f.opacity, 0.05, 1, DEFAULT_FX.opacity),
        colors: colors.length ? colors : DEFAULT_FX.colors,
        interact: f.interact === false ? false : true
    };
}
/** Validate/normalise the optional customisation fields of a designed block (texts/vars/designId are checked by the caller). */
export function normalizeDesignExtras(props, sectionCount = 100, dataSchema) {
    const errors = [];
    const images = {};
    if (props.images !== undefined) {
        if (typeof props.images !== 'object' || props.images === null || Array.isArray(props.images))
            errors.push({ path: 'images', message: 'images must be an object.' });
        else {
            for (const [k, v] of Object.entries(props.images)) {
                const rec = (typeof v === 'object' && v !== null ? v : {});
                if (INDEX_KEY.test(k) && typeof rec.assetId === 'string' && ASSET_ID.test(rec.assetId) && (rec.alt === undefined || (typeof rec.alt === 'string' && rec.alt.length <= 200))) {
                    images[k] = { assetId: rec.assetId, alt: typeof rec.alt === 'string' ? rec.alt : '' };
                }
                else
                    errors.push({ path: `images.${k}`, message: 'Invalid image.' });
            }
        }
    }
    const links = {};
    if (props.links !== undefined) {
        if (typeof props.links !== 'object' || props.links === null || Array.isArray(props.links))
            errors.push({ path: 'links', message: 'links must be an object.' });
        else {
            for (const [k, v] of Object.entries(props.links)) {
                if (INDEX_KEY.test(k) && typeof v === 'string' && isSafeLink(v))
                    links[k] = v.trim();
                else
                    errors.push({ path: `links.${k}`, message: 'Links must start with https://, http://, mailto:, tel:, # or /.' });
            }
        }
    }
    const intList = (v, path) => {
        if (v === undefined)
            return [];
        if (!Array.isArray(v) || v.length > 60 || !v.every((n) => Number.isInteger(n) && n >= 0 && n < sectionCount)) {
            errors.push({ path, message: 'Invalid section list.' });
            return [];
        }
        return Array.from(new Set(v));
    };
    const hidden = intList(props.hidden, 'hidden');
    const order = intList(props.order, 'order');
    let fx;
    if (props.fx !== undefined) {
        if (typeof props.fx !== 'object' || props.fx === null)
            errors.push({ path: 'fx', message: 'Invalid effect settings.' });
        else
            fx = normalizeFx(props.fx);
    }
    let fonts;
    if (props.fonts !== undefined) {
        const f = (typeof props.fonts === 'object' && props.fonts !== null ? props.fonts : {});
        fonts = {};
        for (const key of ['heading', 'body']) {
            const v = f[key];
            if (v === undefined || v === '')
                continue;
            if (typeof v === 'string' && Object.prototype.hasOwnProperty.call(DESIGN_FONTS, v))
                fonts[key] = v;
            else
                errors.push({ path: `fonts.${key}`, message: 'Unknown font.' });
        }
    }
    let data;
    if (props.data !== undefined) {
        if (typeof props.data !== 'object' || props.data === null || Array.isArray(props.data))
            errors.push({ path: 'data', message: 'Invalid content data.' });
        else {
            data = {};
            for (const [name, v] of Object.entries(props.data)) {
                const schema = dataSchema?.find((e) => e.name === name);
                if (dataSchema && !schema) {
                    errors.push({ path: `data.${name}`, message: 'Unknown content list.' });
                    continue;
                }
                const res = normalizeDataValue(v, schema);
                if (res.ok)
                    data[name] = res.value;
                else
                    errors.push({ path: `data.${name}`, message: res.message });
            }
        }
    }
    let favicon;
    if (props.favicon !== undefined && props.favicon !== null) {
        const rec = (typeof props.favicon === 'object' ? props.favicon : {});
        if (typeof rec.assetId === 'string' && ASSET_ID.test(rec.assetId))
            favicon = { assetId: rec.assetId };
        else
            errors.push({ path: 'favicon', message: 'favicon must be an uploaded image.' });
    }
    return { value: { images, links, hidden, order, fx, fonts, data, favicon }, errors };
}
const DATA_NAME = /^[A-Za-z_$][\w$]{0,40}$/;
const DATA_KEY = /^[A-Za-z_$][\w$-]{0,30}$/;
const MAP_KEY = /^[\w &'.+/-]{1,60}$/;
// eslint-disable-next-line no-control-regex
const BAD_TEXT = /[<>"\u0000-\u0008\u000b\u000c\u000e-\u001f\u2028\u2029]/;
/** Item text must be plain: no tags or quotes (the design's own script may place it into HTML attributes). */
export function isSafeDataText(v, max = 600) {
    return typeof v === 'string' && v.length <= max && !BAD_TEXT.test(v);
}
function cleanPrimitive(v, want) {
    if ((want === undefined || want === 'string') && isSafeDataText(v))
        return v;
    if ((want === undefined || want === 'number') && typeof v === 'number' && Number.isFinite(v) && Math.abs(v) < 1e9)
        return v;
    if ((want === undefined || want === 'boolean') && typeof v === 'boolean')
        return v;
    return undefined;
}
function blankFor(type) {
    return type === 'number' ? 0 : type === 'boolean' ? false : type === 'list' ? [] : '';
}
/** Validate one data override against its schema entry (or generically when no schema is given). Returns the cleaned value. */
export function normalizeDataValue(v, schema) {
    if (Array.isArray(v)) {
        if (schema && schema.kind !== 'array')
            return { ok: false, message: 'Expected an object.' };
        if (v.length > 60)
            return { ok: false, message: 'Too many items (max 60).' };
        const fields = schema && schema.kind === 'array' ? schema.fields : undefined;
        const out = [];
        for (const [i, item] of v.entries()) {
            if (typeof item !== 'object' || item === null || Array.isArray(item))
                return { ok: false, message: `Item ${i + 1} is not a record.` };
            const rec = item;
            const keys = fields ? fields.map((f) => f.key) : Object.keys(rec);
            if (!fields && keys.length > 20)
                return { ok: false, message: `Item ${i + 1} has too many fields.` };
            const clean = {};
            for (const key of keys) {
                if (!DATA_KEY.test(key))
                    return { ok: false, message: `Bad field name "${key}".` };
                const f = fields?.find((x) => x.key === key);
                const val = rec[key];
                if (val === undefined) {
                    if (f)
                        clean[key] = blankFor(f.type);
                    continue;
                }
                if (f?.type === 'image') {
                    if (typeof val !== 'string' || (val !== '' && !ASSET_ID.test(val))) {
                        return { ok: false, message: `Item ${i + 1}: "${key}" must be an uploaded image.` };
                    }
                    clean[key] = val;
                    continue;
                }
                if (f?.type === 'list' || (!f && Array.isArray(val))) {
                    if (!Array.isArray(val) || val.length > 12 || !val.every((x) => isSafeDataText(x, 80))) {
                        return { ok: false, message: `Item ${i + 1}: "${key}" must be a short list of plain text.` };
                    }
                    clean[key] = val;
                }
                else {
                    const prim = cleanPrimitive(val, f ? f.type : undefined);
                    if (prim === undefined)
                        return { ok: false, message: `Item ${i + 1}: "${key}" has an invalid value (no < > or " characters).` };
                    clean[key] = prim;
                }
            }
            out.push(clean);
        }
        return { ok: true, value: out };
    }
    if (typeof v === 'object' && v !== null) {
        if (schema && schema.kind !== 'map')
            return { ok: false, message: 'Expected a list.' };
        const entries = Object.entries(v);
        if (entries.length > 40)
            return { ok: false, message: 'Too many entries.' };
        const orig = schema && schema.kind === 'map' ? schema.value : undefined;
        const out = {};
        for (const [key, val] of entries) {
            if (!DATA_KEY.test(key) && !MAP_KEY.test(key))
                return { ok: false, message: `Bad key "${key}".` };
            if (orig && !(key in orig))
                return { ok: false, message: `Unknown entry "${key}".` };
            const ov = orig?.[key];
            if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
                const nested = {};
                for (const [k2, v2] of Object.entries(val)) {
                    const ovNested = ov && typeof ov === 'object' ? ov : undefined;
                    if (ovNested && !(k2 in ovNested))
                        return { ok: false, message: `Unknown entry "${key}.${k2}".` };
                    const want = ovNested ? typeof ovNested[k2] : undefined;
                    const prim = cleanPrimitive(v2, want);
                    if (prim === undefined)
                        return { ok: false, message: `"${key}.${k2}" has an invalid value.` };
                    nested[k2] = prim;
                }
                out[key] = nested;
            }
            else {
                const want = ov !== undefined && typeof ov !== 'object' ? typeof ov : undefined;
                const prim = cleanPrimitive(val, want);
                if (prim === undefined)
                    return { ok: false, message: `"${key}" has an invalid value (no < > or " characters).` };
                out[key] = prim;
            }
        }
        return { ok: true, value: out };
    }
    return { ok: false, message: 'Invalid content data.' };
}
/**
 * List items may carry a `photo`: the id of an uploaded asset. The page's own script needs a URL, so swap it in here.
 * An unknown or missing asset becomes '' (the design's own artwork shows instead).
 */
function resolvePhotos(data, assets) {
    for (const value of Object.values(data)) {
        if (!Array.isArray(value))
            continue;
        for (const item of value) {
            if (!item || typeof item !== 'object' || !('photo' in item))
                continue;
            const rec = item;
            const id = rec['photo'];
            rec['photo'] = typeof id === 'string' && ASSET_ID.test(id) && assets[id] ? assets[id].url : '';
        }
    }
}
/** The data overrides that are safe to inject into the page (generic check, applied again at render time). */
function safeData(data) {
    const out = {};
    for (const [name, v] of Object.entries(data ?? {})) {
        if (!DATA_NAME.test(name))
            continue;
        const res = normalizeDataValue(v);
        if (res.ok)
            out[name] = res.value;
    }
    return out;
}
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function cssUrl(u) {
    return 'url("' + encodeURI(u).replace(/["()\\]/g, '') + '")';
}
/** CSS that hides and reorders sections. */
export function designLayoutCss(props, sectionCount) {
    const rules = [];
    for (const i of props.hidden ?? [])
        if (i >= 0 && i < sectionCount)
            rules.push(`[data-sec="${i}"]{display:none!important}`);
    const order = props.order ?? [];
    if (order.length > 0) {
        const seen = new Set();
        const full = [];
        for (const i of order)
            if (i >= 0 && i < sectionCount && !seen.has(i))
                (seen.add(i), full.push(i));
        for (let i = 0; i < sectionCount; i++)
            if (!seen.has(i))
                full.push(i);
        if (full.some((v, idx) => v !== idx)) {
            rules.push('*:has(>[data-sec]){display:flex!important;flex-direction:column}');
            full.forEach((sec, pos) => rules.push(`[data-sec="${sec}"]{order:${pos + 1}}`));
            // An added section travels with the section it sits under (same order value, later in the page).
            for (const extra of props.extras ?? []) {
                const pos = full.indexOf(extra.after);
                if (pos >= 0 && /^[A-Za-z0-9_-]{1,40}$/.test(extra.id))
                    rules.push(`[data-sarnia-extra="${extra.id}"]{order:${pos + 1}}`);
            }
        }
    }
    return rules.join('');
}
/** Google Fonts stylesheet URL + the CSS applying the chosen fonts. */
export function designFontParts(fonts) {
    if (!fonts)
        return { href: null, css: '' };
    const picked = [fonts.heading, fonts.body].filter((f) => !!f && Object.prototype.hasOwnProperty.call(DESIGN_FONTS, f));
    if (picked.length === 0)
        return { href: null, css: '' };
    const families = Array.from(new Set(picked)).map((f) => 'family=' + DESIGN_FONTS[f]).join('&');
    let css = '';
    if (fonts.heading && DESIGN_FONTS[fonts.heading]) {
        const h = `'${fonts.heading}',sans-serif`;
        css += `:root{--font-display:${h};--font-heading:${h};--font-serif:${h}}h1,h2,h3,h4,h5,h6{font-family:${h}!important}`;
    }
    if (fonts.body && DESIGN_FONTS[fonts.body]) {
        const b = `'${fonts.body}',sans-serif`;
        css += `:root{--font-body:${b};--font-sans:${b}}body,p,li,button,input,textarea,label{font-family:${b}!important}`;
    }
    return { href: `https://fonts.googleapis.com/css2?${families}&display=swap`, css };
}
/** Hides the design's own decorative canvases (used when the effect is turned off or replaced). */
export const HIDE_ORIGINAL_FX_CSS = 'canvas[aria-hidden="true"]:not([data-sarnia-fx]){display:none!important}';
/** Configurable particle engine. Reads window.__SARNIA_FX (a DesignFx) and exposes window.__sarniaFxUpdate(fx). ES5, no deps. */
export const DESIGN_FX_ENGINE = `
(function(){
  var cur=null,canvas=null,ctx=null,raf=0,parts=[],W=0,H=0,mx=-9999,my=-9999,host=null,io=null,visible=true,styleEl=null;
  var reduce=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function pick(a){return a[Math.floor(Math.random()*a.length)];}
  function rnd(a,b){return a+Math.random()*(b-a);}
  function findTarget(){
    var c=document.querySelector('canvas[aria-hidden="true"]:not([data-sarnia-fx])');
    if(c&&c.parentElement)return{host:c.parentElement,ref:c};
    var h=document.querySelector('#hero,.hero,header.hero,[data-sec="1"],[data-sec="0"]');
    return{host:h||document.body,ref:null};
  }
  function mk(){
    var t=findTarget();host=t.host;
    canvas=document.createElement('canvas');canvas.setAttribute('data-sarnia-fx','1');canvas.setAttribute('aria-hidden','true');
    if(t.ref){canvas.className=t.ref.className;canvas.style.cssText=t.ref.style.cssText;t.ref.parentElement.insertBefore(canvas,t.ref.nextSibling);}
    else{
      if(getComputedStyle(host).position==='static')host.style.position='relative';
      host.style.isolation='isolate';
      canvas.style.cssText='position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:-1';
      host.insertBefore(canvas,host.firstChild);
    }
    canvas.style.pointerEvents='none';
    ctx=canvas.getContext('2d');
    host.addEventListener('mousemove',function(e){var r=canvas.getBoundingClientRect();mx=e.clientX-r.left;my=e.clientY-r.top;});
    host.addEventListener('mouseleave',function(){mx=-9999;my=-9999;});
    window.addEventListener('resize',function(){size();seed();});
    if('IntersectionObserver' in window){io=new IntersectionObserver(function(es){visible=es[0].isIntersecting;if(visible)loop();});io.observe(host);}
  }
  function size(){
    if(!canvas)return;var r=host.getBoundingClientRect();W=Math.max(1,Math.round(r.width));H=Math.max(1,Math.round(r.height));
    var dpr=Math.min(2,window.devicePixelRatio||1);canvas.width=W*dpr;canvas.height=H*dpr;ctx.setTransform(dpr,0,0,dpr,0,0);
  }
  function mkPart(){
    var c=cur,s=c.size,col=pick(c.colors),p={x:Math.random()*W,y:Math.random()*H,col:col,r:rnd(s*0.5,s*1.3),ph:Math.random()*6.28,vx:0,vy:0,rot:Math.random()*6.28,vr:rnd(-.05,.05),life:Math.random()};
    switch(c.style){
      case 'snow':p.vy=rnd(.4,1.1);p.vx=rnd(-.3,.3);break;
      case 'rain':p.vy=rnd(7,12);p.vx=-1.5;p.len=rnd(10,22);p.y=Math.random()*H;break;
      case 'bubbles':p.vy=-rnd(.3,.9);p.vx=rnd(-.2,.2);p.r=rnd(s,s*3);break;
      case 'embers':p.vy=-rnd(.4,1.4);p.vx=rnd(-.4,.4);p.r=rnd(s*.4,s);p.y=H+Math.random()*30;break;
      case 'confetti':p.vy=rnd(.8,2);p.vx=rnd(-.5,.5);p.w=rnd(s*1.5,s*3);p.h=rnd(s*.8,s*1.6);break;
      case 'fireflies':p.vx=rnd(-.3,.3);p.vy=rnd(-.3,.3);p.r=rnd(s,s*2);break;
      case 'stars':p.vx=rnd(-.05,.05);p.vy=rnd(-.05,.05);p.r=rnd(s*.3,s*.9);break;
      default:p.vx=rnd(-.4,.4);p.vy=rnd(-.4,.4);
    }
    return p;
  }
  function seed(){if(!cur||!canvas)return;parts=[];var n=cur.count;for(var i=0;i<n;i++)parts.push(mkPart());}
  function step(p){
    var c=cur,k=c.speed;
    if(c.interact&&mx>-999){var dx=p.x-mx,dy=p.y-my,d=Math.sqrt(dx*dx+dy*dy);if(d<110&&d>0.1){var f=(110-d)/110*1.6;p.x+=dx/d*f;p.y+=dy/d*f;}}
    p.ph+=0.03*k;p.rot+=p.vr*k;
    switch(c.style){
      case 'snow':p.x+=(p.vx+Math.sin(p.ph)*.4)*k;p.y+=p.vy*k;break;
      case 'bubbles':p.x+=(p.vx+Math.sin(p.ph)*.3)*k;p.y+=p.vy*k;break;
      case 'fireflies':p.vx+=rnd(-.05,.05);p.vy+=rnd(-.05,.05);p.vx*=.98;p.vy*=.98;p.x+=p.vx*k;p.y+=p.vy*k;break;
      case 'embers':p.x+=(p.vx+Math.sin(p.ph)*.3)*k;p.y+=p.vy*k;p.life+=.004*k;break;
      default:p.x+=p.vx*k;p.y+=p.vy*k;
    }
    var m=40;
    if(c.style==='snow'||c.style==='rain'||c.style==='confetti'){if(p.y>H+m){p.y=-m;p.x=Math.random()*W;}if(p.x<-m)p.x=W+m;if(p.x>W+m)p.x=-m;}
    else if(c.style==='bubbles'||c.style==='embers'){if(p.y<-m||p.life>1.2){p.y=H+m;p.x=Math.random()*W;p.life=0;}if(p.x<-m)p.x=W+m;if(p.x>W+m)p.x=-m;}
    else{if(p.x<-m)p.x=W+m;if(p.x>W+m)p.x=-m;if(p.y<-m)p.y=H+m;if(p.y>H+m)p.y=-m;}
  }
  function draw(p){
    var c=cur,a=c.opacity;ctx.fillStyle=p.col;ctx.strokeStyle=p.col;
    switch(c.style){
      case 'stars':ctx.globalAlpha=a*(0.35+0.65*Math.abs(Math.sin(p.ph)));ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,6.283);ctx.fill();break;
      case 'bubbles':ctx.globalAlpha=a*.5;ctx.lineWidth=1.2;ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,6.283);ctx.stroke();ctx.globalAlpha=a*.12;ctx.fill();break;
      case 'embers':ctx.globalAlpha=a*Math.max(0,1-p.life)*(0.6+0.4*Math.sin(p.ph*3));ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,6.283);ctx.fill();break;
      case 'confetti':ctx.globalAlpha=a;ctx.save();ctx.translate(p.x,p.y);ctx.rotate(p.rot);ctx.fillRect(-p.w/2,-p.h/2,p.w,p.h);ctx.restore();break;
      case 'rain':ctx.globalAlpha=a*.6;ctx.lineWidth=Math.max(1,c.size*.35);ctx.beginPath();ctx.moveTo(p.x,p.y);ctx.lineTo(p.x+1.5,p.y-p.len);ctx.stroke();break;
      case 'fireflies':var g=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,p.r*4);var pulse=0.4+0.6*Math.abs(Math.sin(p.ph));ctx.globalAlpha=a*pulse;g.addColorStop(0,p.col);g.addColorStop(1,'rgba(0,0,0,0)');ctx.fillStyle=g;ctx.beginPath();ctx.arc(p.x,p.y,p.r*4,0,6.283);ctx.fill();break;
      default:ctx.globalAlpha=a;ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,6.283);ctx.fill();
    }
  }
  function lines(){
    ctx.lineWidth=1;
    for(var i=0;i<parts.length;i++)for(var j=i+1;j<parts.length;j++){
      var dx=parts[i].x-parts[j].x,dy=parts[i].y-parts[j].y,d=dx*dx+dy*dy,max=130*130;
      if(d<max){ctx.globalAlpha=cur.opacity*(1-d/max)*.6;ctx.strokeStyle=parts[i].col;ctx.beginPath();ctx.moveTo(parts[i].x,parts[i].y);ctx.lineTo(parts[j].x,parts[j].y);ctx.stroke();}
    }
  }
  function frame(){
    ctx.clearRect(0,0,W,H);
    if(cur.style==='network'&&parts.length<160)lines();
    for(var i=0;i<parts.length;i++){if(!reduce)step(parts[i]);draw(parts[i]);}
    ctx.globalAlpha=1;
  }
  function loop(){
    cancelAnimationFrame(raf);
    if(!cur||cur.mode!=='custom'||!visible||document.hidden)return;
    frame();
    if(!reduce)raf=requestAnimationFrame(loop);
  }
  document.addEventListener('visibilitychange',function(){if(!document.hidden)loop();});
  function applyCss(){
    if(!styleEl){styleEl=document.createElement('style');styleEl.id='sarnia-fx-css';document.head.appendChild(styleEl);}
    styleEl.textContent=(cur&&cur.mode!=='original')?'canvas[aria-hidden="true"]:not([data-sarnia-fx]){display:none!important}':'';
  }
  window.__sarniaFxUpdate=function(fx){
    cur=fx;applyCss();
    if(!cur||cur.mode!=='custom'){cancelAnimationFrame(raf);if(canvas&&ctx)ctx.clearRect(0,0,W,H);return;}
    if(!canvas)mk();
    size();seed();loop();
  };
  function boot(){if(window.__SARNIA_FX)window.__sarniaFxUpdate(window.__SARNIA_FX);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot);else boot();
})();`;
const PUBLISH_CSP = "default-src 'self'; script-src 'unsafe-inline'; style-src 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com data:; img-src 'self' data: blob:; connect-src 'none'; form-action 'none'; base-uri 'none'; object-src 'none'";
/** Script injected into the editor preview (sandboxed, opaque-origin iframe). It talks to the editor via postMessage. */
export const DESIGN_EDITOR_SCRIPT = `
(function(){
  var send=function(m){m.source='sarnia-design';parent.postMessage(m,'*');};
  var st=document.createElement('style');
  st.textContent='[data-ed]{cursor:text}[data-ed]:hover{outline:1px dashed #6366f1;outline-offset:2px}[data-ed][contenteditable]{outline:2px solid #6366f1!important;outline-offset:2px;min-width:1ch}[data-img]{cursor:pointer}[data-img]:hover{outline:2px dashed #ec4899;outline-offset:-2px}.sarnia-pulse{outline:3px solid #f59e0b!important;outline-offset:2px;transition:outline-color .3s}';
  document.head.appendChild(st);
  var active=null,original='';
  function norm(el){return el.textContent.replace(/\\s+/g,' ').trim();}
  function finish(commit){
    if(!active)return;var el=active;active=null;el.removeAttribute('contenteditable');
    if(!commit){el.textContent=original;return;}
    var text=norm(el);
    if(text!==original){send({type:'text',id:el.getAttribute('data-ed'),text:text});}
  }
  function matchItem(T){
    var D=window.__SARNIA_ITEMS;if(!D)return null;
    var el=T,depth=0;
    while(el&&el!==document.body&&el!==document.documentElement&&depth<9){
      var txt=(el.textContent||'').replace(/\\s+/g,' ');
      if(txt.length<2500){
        var best=null,bs=0,tie=false;
        for(var n in D){var arr=D[n];if(!Array.isArray(arr))continue;
          for(var i=0;i<arr.length;i++){var it=arr[i],s=0;
            for(var k in it){var v=it[k];if(typeof v==='string'&&v.length>=2&&txt.indexOf(v)>-1)s++;}
            if(s>bs){bs=s;best={n:n,i:i};tie=false;}else if(s===bs&&s>0){tie=true;}
          }}
        if(best&&!tie)return best;
      }
      el=el.parentElement;depth++;
    }
    return null;
  }
  document.addEventListener('click',function(e){
    var T=e.target&&e.target.closest?e.target:null;if(!T)return;
    var t=T.closest('[data-ed]'),im=T.closest('[data-img]'),a=T.closest('a,button,form');
    if(a)e.preventDefault();
    if(t&&t===active)return;
    if(t){
      finish(true);active=t;original=norm(t);
      t.setAttribute('contenteditable','plaintext-only');t.focus();
      var r=document.createRange();r.selectNodeContents(t);var s=getSelection();s.removeAllRanges();s.addRange(r);
      return;
    }
    finish(true);
    if(im){send({type:'image',id:im.getAttribute('data-img')});return;}
    var l=T.closest('[data-lnk]');if(l){send({type:'link',id:l.getAttribute('data-lnk')});return;}
    var m=matchItem(T);if(m)send({type:'item',name:m.n,index:m.i});
  },true);
  document.addEventListener('submit',function(e){e.preventDefault();},true);
  document.addEventListener('focusout',function(e){if(active&&e.target===active)finish(true);},true);
  document.addEventListener('keydown',function(e){
    if(!active)return;
    if(e.key==='Enter'){e.preventDefault();active.blur();}
    if(e.key==='Escape'){e.preventDefault();finish(false);}
  },true);
  var scrollTimer=0;
  window.addEventListener('scroll',function(){clearTimeout(scrollTimer);scrollTimer=setTimeout(function(){send({type:'scroll',y:window.scrollY});},120);},{passive:true});
  var stash={};
  function setImage(id,img){
    var el=document.querySelector('[data-img="'+id+'"]');if(!el)return;
    if(!img){
      if(stash[id]){var tmp=document.createElement('div');tmp.innerHTML=stash[id].html;var orig=tmp.firstElementChild;if(orig){el.replaceWith(orig);}delete stash[id];}
      return;
    }
    if(el.tagName.toLowerCase()==='svg'||stash[id]&&stash[id].kind==='svg'){
      if(!stash[id])stash[id]={kind:'svg',html:el.outerHTML};
      var vb=(stash[id].html.match(/viewBox="([^"]+)"/)||[])[1],ar='';
      if(vb){var p=vb.split(/[\\s,]+/);ar='aspect-ratio:'+p[2]+'/'+p[3]+';';}
      var cls=(stash[id].html.match(/<svg[^>]*\\sclass="([^"]*)"/)||[])[1]||'';
      var ni=document.createElement('img');ni.src=img.url;ni.alt=img.alt||'';ni.className=cls;ni.setAttribute('data-img',id);
      var fw=(stash[id].html.match(/data-img-w="(\\d+)"/)||[])[1],fh=(stash[id].html.match(/data-img-h="(\\d+)"/)||[])[1];
      if(fw&&fh){ni.setAttribute('data-img-w',fw);ni.setAttribute('data-img-h',fh);ni.style.cssText='width:'+fw+'px;height:'+fh+'px;object-fit:contain;max-width:100%';}
      else{ni.style.cssText='width:100%;height:auto;'+ar+'object-fit:cover;display:block;max-width:100%';}
      el.replaceWith(ni);
    }else{
      if(!stash[id])stash[id]={kind:'box',html:''};
      el.style.setProperty('background-image','url("'+img.url.replace(/"/g,'')+'")','important');el.style.setProperty('background-size','cover','important');el.style.setProperty('background-position','center','important');
      Array.prototype.forEach.call(el.children,function(c){c.style.visibility='hidden';});
    }
  }
  function clearBox(id){var el=document.querySelector('[data-img="'+id+'"]');if(el&&stash[id]&&stash[id].kind==='box'){el.style.removeProperty('background-image');el.style.removeProperty('background-size');el.style.removeProperty('background-position');Array.prototype.forEach.call(el.children,function(c){c.style.visibility='';});delete stash[id];}}
  function styleTag(id){var s=document.getElementById(id);if(!s){s=document.createElement('style');s.id=id;document.head.appendChild(s);}return s;}
  window.addEventListener('message',function(e){
    var d=e.data;if(!d||d.target!=='sarnia-design')return;
    var root=document.documentElement;
    if(d.type==='vars'){for(var k in d.vars){root.style.setProperty(k,d.vars[k]);}}
    if(d.type==='unvars'){(d.names||[]).forEach(function(n){root.style.removeProperty(n);});}
    if(d.type==='scrollTo'){window.scrollTo(0,d.y||0);setTimeout(function(){window.scrollTo(0,d.y||0);},350);}
    if(d.type==='layout'){styleTag('sarnia-layout-css').textContent=d.css||'';}
    if(d.type==='fonts'){
      var lk=document.getElementById('sarnia-fonts-link');
      if(d.href){if(!lk){lk=document.createElement('link');lk.id='sarnia-fonts-link';lk.rel='stylesheet';document.head.appendChild(lk);}lk.href=d.href;}else if(lk){lk.remove();}
      styleTag('sarnia-fonts-css').textContent=d.css||'';
    }
    if(d.type==='fx'){window.__SARNIA_FX=d.fx;if(window.__sarniaFxUpdate)window.__sarniaFxUpdate(d.fx);}
    if(d.type==='setImage'){clearBox(d.id);setImage(d.id,d.url?{url:d.url,alt:d.alt}:null);}
    if(d.type==='setLink'){var a=document.querySelector('[data-lnk="'+d.id+'"]');if(a&&d.href)a.setAttribute('href',d.href);}
    if(d.type==='focus'){var el=document.querySelector(d.selector);if(el){el.scrollIntoView({behavior:'smooth',block:'center'});el.classList.add('sarnia-pulse');setTimeout(function(){el.classList.remove('sarnia-pulse');},1400);}}
  });
  send({type:'ready'});
})();`;
/** The loading screen is visible in the markup; the page scripts hide it. Without them it stays on screen. */
function loaderViewHtml(html) {
    return html
        .replace(/<script\b[\s\S]*?<\/script>/gi, '')
        .replace(/(<html\b[^>]*\bclass=")([^"]*)"/i, (_m, a, c) => `${a}${c.split(/\s+/).filter((x) => x && x !== 'no-js').join(' ')}"`);
}
/** Find the end of an <svg ...> element starting at `start` (handles nested svgs). Returns the index after </svg>. */
function svgEnd(html, start) {
    const re = /<svg\b|<\/svg\s*>/gi;
    re.lastIndex = start + 1;
    let depth = 1;
    let m;
    while ((m = re.exec(html))) {
        if (m[0][1] === '/') {
            depth -= 1;
            if (depth === 0)
                return m.index + m[0].length;
        }
        else
            depth += 1;
    }
    return -1;
}
function attrOf(tag, name) {
    const m = tag.match(new RegExp('\\s' + name + '="([^"]*)"'));
    return m ? m[1] : null;
}
function applyImages(html, images, assets, editor) {
    let css = '';
    let out = html;
    for (const [id, img] of Object.entries(images)) {
        const asset = assets[img.assetId];
        if (!asset)
            continue;
        const re = new RegExp('<([a-zA-Z][a-zA-Z0-9]*)\\b[^>]*\\sdata-img="' + id + '"[^>]*>');
        const m = re.exec(out);
        if (!m)
            continue;
        const alt = esc(img.alt || asset.alt || '');
        if (m[1].toLowerCase() === 'svg') {
            const end = svgEnd(out, m.index);
            if (end < 0)
                continue;
            const tag = m[0];
            const vb = attrOf(tag, 'viewBox');
            const parts = vb ? vb.split(/[\s,]+/) : [];
            const ratio = parts.length === 4 ? `aspect-ratio:${Number(parts[2]) || 4}/${Number(parts[3]) || 3};` : '';
            const cls = attrOf(tag, 'class');
            // Logos and icons carry their rendered size (data-img-w/h) so a replacement keeps it instead of stretching to the container.
            const fw = Number(attrOf(tag, 'data-img-w'));
            const fh = Number(attrOf(tag, 'data-img-h'));
            const fixed = fw > 0 && fw <= 400 && fh > 0 && fh <= 400;
            const style = fixed ? `width:${fw}px;height:${fh}px;object-fit:contain;max-width:100%` : `width:100%;height:auto;${ratio}object-fit:cover;display:block;max-width:100%`;
            const repl = `<img src="${esc(asset.url)}" alt="${alt}"${cls ? ` class="${cls}"` : ''}${editor ? ` data-img="${id}"${fixed ? ` data-img-w="${fw}" data-img-h="${fh}"` : ''}` : ''} style="${style}">`;
            out = out.slice(0, m.index) + repl + out.slice(end);
        }
        else {
            css += `[data-img="${id}"]{background-image:${cssUrl(asset.url)}!important;background-size:cover!important;background-position:center!important}[data-img="${id}"]>*{visibility:hidden}`;
        }
    }
    return { html: out, css };
}
function applyLinks(html, links) {
    let out = html;
    for (const [id, url] of Object.entries(links)) {
        if (!isSafeLink(url))
            continue;
        const re = new RegExp('(<a\\b[^>]*\\sdata-lnk="' + id + '"[^>]*>)');
        out = out.replace(re, (tag) => (/\shref="[^"]*"/.test(tag) ? tag.replace(/(\shref=)"[^"]*"/, (_m, p) => `${p}"${esc(url.trim())}"`) : tag));
    }
    return out;
}
/** Merge a customer's edits into the pristine design HTML. Pure string work, no DOM needed. */
export function applyDesign(html, props, meta, options = {}) {
    const editor = options.editor === true;
    const texts = props.texts ?? {};
    if (editor && options.view === 'loader')
        html = loaderViewHtml(html);
    let out = html.replace(/<span data-ed="(\d+)">([\s\S]*?)<\/span>/g, (_m, id, original) => {
        const override = Object.prototype.hasOwnProperty.call(texts, id) && isValidDesignText(id, texts[id]) ? texts[id] : undefined;
        const content = override !== undefined ? esc(override) : original;
        return editor ? `<span data-ed="${id}">${content}</span>` : content;
    });
    const imagesProp = {};
    for (const [k, v] of Object.entries(props.images ?? {}))
        if (INDEX_KEY.test(k) && v && ASSET_ID.test(v.assetId))
            imagesProp[k] = v;
    const imgResult = applyImages(out, imagesProp, options.assets ?? {}, editor);
    out = imgResult.html;
    const linksProp = {};
    for (const [k, v] of Object.entries(props.links ?? {}))
        if (INDEX_KEY.test(k) && isSafeLink(v))
            linksProp[k] = v;
    out = applyLinks(out, linksProp);
    if (options.extraHtml && options.extraHtml.length > 0)
        out = insertExtras(out, options.extraHtml);
    const sectionCount = options.sectionCount ?? 100;
    const layoutCss = designLayoutCss(props, sectionCount);
    const fontParts = designFontParts(props.fonts);
    const fx = props.fx ? normalizeFx(props.fx) : null;
    const vars = Object.entries(props.vars ?? {}).filter(([k, v]) => isValidDesignVar(k, v));
    const head = [];
    const dataOverrides = safeData(props.data);
    resolvePhotos(dataOverrides, options.assets ?? {});
    if (Object.keys(dataOverrides).length > 0) {
        // Plain data the page's own script reads (window.__SARNIA_DATA); it sits in <head>, before the page scripts.
        head.push(`<script>window.__SARNIA_DATA=${JSON.stringify(dataOverrides).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029')};</script>`);
    }
    const hasPhotos = Object.values(dataOverrides).some((list) => Array.isArray(list) && list.some((it) => it && typeof it === 'object' && typeof it['photo'] === 'string' && it['photo'] !== ''));
    // A card with an uploaded picture hides the design's own overlay decorations (the page script adds .has-photo).
    if (hasPhotos)
        head.push('<style id="sarnia-photo-css">.has-photo::before,.has-photo::after{display:none!important}</style>');
    if (vars.length > 0)
        head.push(`<style id="sarnia-vars">:root{${vars.map(([k, v]) => `${k}:${v.trim()}`).join(';')}}</style>`);
    const extraCss = (imgResult.css + layoutCss).trim();
    if (extraCss)
        head.push(`<style id="sarnia-layout-css">${extraCss}</style>`);
    if (fontParts.href)
        head.push(`<link id="sarnia-fonts-link" rel="stylesheet" href="${esc(fontParts.href)}">`);
    if (fontParts.css)
        head.push(`<style id="sarnia-fonts-css">${fontParts.css}</style>`);
    if (!editor) {
        if (fx && fx.mode !== 'original')
            head.push(`<style>${HIDE_ORIGINAL_FX_CSS}</style>`);
        if (options.csp !== false)
            head.push(`<meta http-equiv="Content-Security-Policy" content="${esc(PUBLISH_CSP)}">`);
        const logo = props.favicon ? options.assets?.[props.favicon.assetId]?.url : undefined;
        const jsonLd = businessJsonLd(props.business, meta, {
            url: options.siteUrl,
            image: logo && options.siteUrl ? new URL(logo, options.siteUrl.replace(/\/?$/, '/')).href : undefined
        });
        if (jsonLd)
            head.push(`<script type="application/ld+json">${jsonLd}</script>`);
    }
    if (head.length)
        out = out.replace(/<\/head>/i, () => `${head.join('')}</head>`);
    if (meta.title)
        out = out.replace(/<title>[\s\S]*?<\/title>/i, () => `<title>${esc(meta.title)}</title>`);
    if (meta.description !== undefined) {
        const tag = `<meta name="description" content="${esc(meta.description)}">`;
        out = /<meta[^>]+name=["']description["'][^>]*>/i.test(out)
            ? out.replace(/<meta[^>]+name=["']description["'][^>]*>/i, () => tag)
            : out.replace(/<\/head>/i, () => `${tag}</head>`);
    }
    if (!editor && meta.title) {
        // Link previews (social, chat apps): the template's own og/twitter tags describe the demo business, so replace them with the owner's title and description.
        const re = /<meta\s[^>]*(?:property|name)=["'](?:og:[a-z_:]+|twitter:[a-z_:]+)["'][^>]*>\s*/gi;
        out = out.replace(re, '');
        const desc = meta.description ?? '';
        const tags = [
            `<meta property="og:title" content="${esc(meta.title)}">`,
            desc ? `<meta property="og:description" content="${esc(desc)}">` : '',
            '<meta property="og:type" content="website">',
            '<meta name="twitter:card" content="summary">',
            `<meta name="twitter:title" content="${esc(meta.title)}">`,
            desc ? `<meta name="twitter:description" content="${esc(desc)}">` : ''
        ].filter(Boolean).join('');
        out = out.replace(/<\/head>/i, () => `${tags}</head>`);
    }
    if (meta.language && /^[a-zA-Z-]{2,10}$/.test(meta.language)) {
        out = /<html[^>]*\blang=/i.test(out)
            ? out.replace(/(<html[^>]*\blang=)["'][^"']*["']/i, (_m, p) => `${p}"${meta.language}"`)
            : out.replace(/<html/i, () => `<html lang="${meta.language}"`);
    }
    if (editor && options.items && Object.keys(options.items).length > 0) {
        // Editor-only lookup for click-to-edit on list cards (never included in published pages).
        const lookup = {};
        for (const [name, list] of Object.entries(options.items)) {
            if (!/^[A-Za-z_$][\w$]{0,60}$/.test(name) || !Array.isArray(list))
                continue;
            lookup[name] = list.slice(0, 60).map((item) => {
                const row = {};
                for (const [k, v] of Object.entries(item ?? {}))
                    if (typeof v === 'string' && k !== 'photo')
                        row[k] = v.slice(0, 300);
                return row;
            });
        }
        out = out.replace(/<\/head>/i, () => `<script>window.__SARNIA_ITEMS=${JSON.stringify(lookup).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029')};</script></head>`);
    }
    if (!editor && props.favicon && options.assets?.[props.favicon.assetId]) {
        // The owner's own site icon replaces whatever icon the template shipped with.
        const icon = `<link rel="icon" href="${esc(options.assets[props.favicon.assetId].url)}">`;
        out = out.replace(/<link[^>]+rel=["'](?:shortcut )?icon["'][^>]*>\s*/gi, '');
        out = out.replace(/<\/head>/i, () => `${icon}</head>`);
    }
    if (editor) {
        const boot = fx ? `window.__SARNIA_FX=${JSON.stringify(fx).replace(/</g, '\\u003c')};` : '';
        out = out.replace(/<\/body>/i, () => `<script>${boot}${DESIGN_FX_ENGINE}</script><script>${DESIGN_EDITOR_SCRIPT}</script></body>`);
    }
    else {
        // Published page: link markers are not needed (data-sec / data-img stay: the layout and image CSS keys on them).
        out = out.replace(/\sdata-lnk="\d+"/g, '');
        if (fx && fx.mode === 'custom') {
            const cfg = JSON.stringify(fx).replace(/</g, '\\u003c');
            out = out.replace(/<\/body>/i, () => `<script>window.__SARNIA_FX=${cfg};${DESIGN_FX_ENGINE}</script></body>`);
        }
    }
    return out;
}
//# sourceMappingURL=design.js.map